/**
 * Priority-list view model.
 *
 * Maps the dashboard status foundation's items into the rows the "Needs your
 * attention" table renders. The endpoint is the single source of status, so
 * this only filters, labels, and orders — it never derives a band.
 */

import type { DashboardStatusItemDto } from "@/generated/model";
import { DashboardStatusItemDtoBand } from "@/generated/model";
import { type PriorityLinkKind, TYPE_META } from "@/lib/dashboard/category-map";
import { getInitials } from "@/lib/dashboard/traffic-light";
import { formatUkDate, isUkToday, isUkTomorrow } from "@/lib/date-utils";

export interface PriorityLink {
  kind: PriorityLinkKind;
  id: string;
}

export type PriorityBand = "ACTION" | "NEEDS_ATTENTION";

export interface PriorityRow {
  id: string;
  title: string;
  /** Area / category subtext, e.g. "Fire Safety". */
  area?: string;
  /** Column label: Checklists / Monitoring / Compliance / Follow-up actions. */
  typeLabel: string;
  /** Named person, or the role when assigned by role. */
  assignee?: string;
  assigneeInitials?: string;
  /** Due date, or the triggering detail when there is no due date. */
  dueLabel: string;
  dueSubLabel?: string;
  overdue: boolean;
  /** Completed work still awaiting the manager's sign-off (follow-up actions). */
  awaitingSignOff: boolean;
  /**
   * Something still needs acknowledging. Two sources since NAU-410: a completed
   * checklist whose failed checks need signing off (NAU-71), and a document sent
   * to staff for read-and-sign.
   */
  needsAcknowledgement: boolean;
  band: PriorityBand;
  link: PriorityLink;
}

const BAND_RANK: Record<PriorityBand, number> = {
  ACTION: 0,
  NEEDS_ATTENTION: 1,
};

/** Bands that belong on the priority list (everything else is filtered out). */
function toPriorityBand(
  band: DashboardStatusItemDto["band"],
): PriorityBand | null {
  if (band === DashboardStatusItemDtoBand.ACTION) return "ACTION";
  if (band === DashboardStatusItemDtoBand.NEEDS_ATTENTION)
    return "NEEDS_ATTENTION";
  return null;
}

function describeDue(
  dueDate: string | null | undefined,
  triggerDetail: string | undefined,
  band: PriorityBand,
): {
  dueLabel: string;
  dueSubLabel?: string;
  overdue: boolean;
  sortKey: number;
} {
  // No due date (e.g. a monitoring reading) — show the triggering detail. An
  // unresolved Action item sorts to the very top of its band.
  if (!dueDate) {
    return {
      dueLabel: triggerDetail ?? "—",
      dueSubLabel: band === "ACTION" ? "Unresolved" : undefined,
      overdue: band === "ACTION",
      sortKey: Number.NEGATIVE_INFINITY,
    };
  }

  const deadline = new Date(dueDate);
  const now = new Date();
  const sortKey = deadline.getTime();

  if (now > deadline) {
    const days = Math.floor((now.getTime() - deadline.getTime()) / 86_400_000);
    return {
      dueLabel: formatUkDate(deadline, "d MMM yyyy"),
      dueSubLabel:
        days >= 1
          ? `${days} day${days > 1 ? "s" : ""} overdue`
          : "Overdue today",
      overdue: true,
      sortKey,
    };
  }
  if (isUkToday(deadline)) {
    return {
      dueLabel: `Today, ${formatUkDate(deadline, "h:mmaaa")}`,
      dueSubLabel: "Due today",
      overdue: false,
      sortKey,
    };
  }
  if (isUkTomorrow(deadline)) {
    return {
      dueLabel: `Tomorrow, ${formatUkDate(deadline, "h:mmaaa")}`,
      dueSubLabel: "Due tomorrow",
      overdue: false,
      sortKey,
    };
  }
  return {
    dueLabel: formatUkDate(deadline, "d MMM yyyy"),
    dueSubLabel: "Due soon",
    overdue: false,
    sortKey,
  };
}

/**
 * Build the ordered priority rows: Action band above Needs-attention, and
 * most-overdue first within each band.
 */
export function mapToPriorityRows(
  items: DashboardStatusItemDto[],
): PriorityRow[] {
  const rows = items.map((item) => {
    const band = item.live ? toPriorityBand(item.band) : null;
    if (!band) return null;
    const meta = TYPE_META[item.type] ?? {
      label: item.type,
      kind: "checklist" as const,
    };
    const assignee = item.assigneeName ?? item.assigneeRole;
    const due = describeDue(item.dueDate, item.triggerDetail, band);
    // A sign-off is completed work, so the lateness wording ("overdue"/"due
    // soon") would mislead — drop the sub-label and the red overdue styling; the
    // "Awaiting sign-off" badge carries the prompt. The due date stays as context.
    const awaitingSignOff = item.awaitingSignOff ?? false;
    return {
      row: {
        id: item.id,
        title: item.title ?? "Untitled",
        area: item.subtitle,
        typeLabel: meta.label,
        assignee,
        assigneeInitials: item.assigneeName
          ? getInitials(item.assigneeName)
          : undefined,
        dueLabel: due.dueLabel,
        dueSubLabel: awaitingSignOff ? undefined : due.dueSubLabel,
        overdue: awaitingSignOff ? false : due.overdue,
        awaitingSignOff,
        needsAcknowledgement: item.needsAcknowledgement ?? false,
        band,
        link: { kind: meta.kind, id: item.id },
      } satisfies PriorityRow,
      sortKey: due.sortKey,
    };
  });

  return rows
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .sort(
      (a, b) =>
        BAND_RANK[a.row.band] - BAND_RANK[b.row.band] || a.sortKey - b.sortKey,
    )
    .map((r) => r.row);
}
